/**
 * Redux slice for managing ORBAT (Order of Battle) state.
 * Handles factions, unit groups, and unit tree data.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    OrbatState,
    Faction,
    FactionCreate,
    FactionUpdate,
    UnitGroup,
    UnitGroupCreate,
    UnitGroupTreeResponse,
    UnitCreate,
    UnitUpdate,
    UnitPositionUpdate,
    UnitTreeNode,
    AffiliationType,
} from '../types/orbat';
import { MobileCredential } from '../types/mobileCredentials';
import * as orbatService from '../services/orbatService';

// =============================================================================
// Initial State
// =============================================================================

const initialState: OrbatState = {
    // Affiliations
    affiliations: [],
    affiliationsLoading: false,
    affiliationsError: null,

    // Factions
    factions: [],
    selectedFactionId: null,
    factionsLoading: false,
    factionsError: null,

    // Unit Groups
    unitGroups: [],
    selectedUnitGroupId: null,
    unitGroupsLoading: false,
    unitGroupsError: null,

    // Unit Tree
    selectedUnitGroupTree: null,
    treeLoading: false,
    treeError: null,

    // General
    isLoading: false,
    error: null,
};

// =============================================================================
// Helper for error extraction
// =============================================================================

function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: unknown } } };
        const detail = axiosError.response?.data?.detail;

        // Handle string detail (simple error message)
        if (typeof detail === 'string') {
            return detail;
        }

        // Handle array of validation errors (Pydantic format)
        if (Array.isArray(detail)) {
            // Extract human-readable messages from validation errors
            const messages = detail.map((err: { msg?: string; loc?: string[] }) => {
                const field = err.loc?.slice(-1)[0] || 'field';
                const msg = err.msg || 'validation error';
                return `${field}: ${msg}`;
            });
            return messages.join('; ') || 'Validation error';
        }

        // Handle object with message property
        if (detail && typeof detail === 'object' && 'message' in detail) {
            return String((detail as { message: unknown }).message);
        }

        return 'An error occurred';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An error occurred';
}

// =============================================================================
// Async Thunks - Affiliations
// =============================================================================

export const fetchAffiliations = createAsyncThunk<
    AffiliationType[],
    void,
    { rejectValue: string }
>('orbat/fetchAffiliations', async (_, { rejectWithValue }) => {
    try {
        const response = await orbatService.getAffiliations();
        return response.affiliations;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Async Thunks - Factions
// =============================================================================

export const fetchFactions = createAsyncThunk<
    Faction[],
    string,
    { rejectValue: string }
>('orbat/fetchFactions', async (eventId, { rejectWithValue }) => {
    try {
        const response = await orbatService.getFactions(eventId);
        return response.factions;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const createFaction = createAsyncThunk<
    Faction,
    { eventId: string; data: FactionCreate },
    { rejectValue: string }
>('orbat/createFaction', async ({ eventId, data }, { rejectWithValue }) => {
    try {
        return await orbatService.createFaction(eventId, data);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const updateFaction = createAsyncThunk<
    Faction,
    { eventId: string; factionId: string; data: FactionUpdate },
    { rejectValue: string }
>('orbat/updateFaction', async ({ eventId, factionId, data }, { rejectWithValue }) => {
    try {
        return await orbatService.updateFaction(eventId, factionId, data);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const deleteFaction = createAsyncThunk<
    string,
    { eventId: string; factionId: string },
    { rejectValue: string }
>('orbat/deleteFaction', async ({ eventId, factionId }, { rejectWithValue }) => {
    try {
        await orbatService.deleteFaction(eventId, factionId);
        return factionId;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Async Thunks - Unit Groups
// =============================================================================

export const fetchUnitGroups = createAsyncThunk<
    UnitGroup[],
    { eventId: string; factionId?: string },
    { rejectValue: string }
>('orbat/fetchUnitGroups', async ({ eventId, factionId }, { rejectWithValue }) => {
    try {
        const response = await orbatService.getUnitGroups(eventId, factionId);
        return response.unit_groups;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const fetchUnitGroupTree = createAsyncThunk<
    UnitGroupTreeResponse,
    { eventId: string; unitGroupId: string },
    { rejectValue: string }
>('orbat/fetchUnitGroupTree', async ({ eventId, unitGroupId }, { rejectWithValue }) => {
    try {
        return await orbatService.getUnitGroupTree(eventId, unitGroupId);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const createUnitGroup = createAsyncThunk<
    UnitGroup,
    { eventId: string; data: UnitGroupCreate },
    { rejectValue: string }
>('orbat/createUnitGroup', async ({ eventId, data }, { rejectWithValue }) => {
    try {
        return await orbatService.createUnitGroup(eventId, data);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const deleteUnitGroup = createAsyncThunk<
    string,
    { eventId: string; unitGroupId: string },
    { rejectValue: string }
>('orbat/deleteUnitGroup', async ({ eventId, unitGroupId }, { rejectWithValue }) => {
    try {
        await orbatService.deleteUnitGroup(eventId, unitGroupId);
        return unitGroupId;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Async Thunks - Units
// =============================================================================

export const createUnit = createAsyncThunk<
    { unit: ReturnType<typeof orbatService.createUnit> extends Promise<infer T> ? T : never; eventId: string; unitGroupId?: string },
    { eventId: string; data: UnitCreate },
    { rejectValue: string }
>('orbat/createUnit', async ({ eventId, data }, { rejectWithValue }) => {
    try {
        const unit = await orbatService.createUnit(eventId, data);
        return { unit, eventId, unitGroupId: data.unit_group_id };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const updateUnit = createAsyncThunk<
    { eventId: string; unitGroupId: string },
    { eventId: string; unitId: string; unitGroupId: string; data: UnitUpdate },
    { rejectValue: string }
>('orbat/updateUnit', async ({ eventId, unitId, unitGroupId, data }, { rejectWithValue }) => {
    try {
        await orbatService.updateUnit(eventId, unitId, data);
        return { eventId, unitGroupId };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const updateUnitPosition = createAsyncThunk<
    { eventId: string; unitGroupId: string },
    { eventId: string; unitId: string; unitGroupId: string; data: UnitPositionUpdate },
    { rejectValue: string }
>('orbat/updateUnitPosition', async ({ eventId, unitId, unitGroupId, data }, { rejectWithValue }) => {
    try {
        await orbatService.updateUnitPosition(eventId, unitId, data);
        return { eventId, unitGroupId };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const reorderUnits = createAsyncThunk<
    { eventId: string; unitGroupId: string },
    { eventId: string; unitGroupId: string; orderedUnitIds: string[] },
    { rejectValue: string }
>('orbat/reorderUnits', async ({ eventId, unitGroupId, orderedUnitIds }, { rejectWithValue }) => {
    try {
        await orbatService.reorderUnits(eventId, orderedUnitIds);
        return { eventId, unitGroupId };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const deleteUnit = createAsyncThunk<
    { eventId: string; unitGroupId: string },
    { eventId: string; unitId: string; unitGroupId: string },
    { rejectValue: string }
>('orbat/deleteUnit', async ({ eventId, unitId, unitGroupId }, { rejectWithValue }) => {
    try {
        await orbatService.deleteUnit(eventId, unitId);
        return { eventId, unitGroupId };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const provisionMobileCredentials = createAsyncThunk<
    MobileCredential,
    { eventId: string; unitId: string },
    { rejectValue: string }
>('orbat/provisionMobileCredentials', async ({ eventId, unitId }, { rejectWithValue }) => {
    try {
        const credential = await orbatService.provisionMobileCredentials(eventId, unitId);
        return credential;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Slice
// =============================================================================

export const orbatSlice = createSlice({
    name: 'orbat',
    initialState,
    reducers: {
        setSelectedFaction: (state, action: PayloadAction<string | null>) => {
            state.selectedFactionId = action.payload;
            // Clear unit group selection when faction changes
            state.selectedUnitGroupId = null;
            state.selectedUnitGroupTree = null;
        },
        setSelectedUnitGroup: (state, action: PayloadAction<string | null>) => {
            state.selectedUnitGroupId = action.payload;
            if (action.payload === null) {
                state.selectedUnitGroupTree = null;
            }
        },
        clearError: (state) => {
            state.error = null;
            state.factionsError = null;
            state.unitGroupsError = null;
            state.treeError = null;
        },
        clearOrbatState: (state) => {
            Object.assign(state, initialState);
        },
        // Optimistic update for unit position change
        optimisticUpdateTree: (state, action: PayloadAction<{
            unitId: string;
            newParentId: string | null;
            newOrder: number;
        }>) => {
            if (!state.selectedUnitGroupTree?.tree) return;

            const { unitId, newParentId, newOrder } = action.payload;

            // Helper to find and remove a node from the tree
            const removeNode = (nodes: UnitTreeNode[], targetId: string): UnitTreeNode | null => {
                for (let i = 0; i < nodes.length; i++) {
                    if (nodes[i].id === targetId) {
                        const [removed] = nodes.splice(i, 1);
                        return removed;
                    }
                    const found = removeNode(nodes[i].children, targetId);
                    if (found) return found;
                }
                return null;
            };

            // Helper to find a node by ID
            const findNode = (node: UnitTreeNode, targetId: string): UnitTreeNode | null => {
                if (node.id === targetId) return node;
                for (const child of node.children) {
                    const found = findNode(child, targetId);
                    if (found) return found;
                }
                return null;
            };

            // Helper to insert node at position
            const insertNode = (
                parentNode: UnitTreeNode | null,
                node: UnitTreeNode,
                order: number,
                tree: UnitTreeNode
            ) => {
                const targetChildren = parentNode ? parentNode.children : [tree];
                node.unit_order = order;

                // Find insertion point
                let insertIndex = targetChildren.findIndex(
                    (child) => child.unit_order > order
                );
                if (insertIndex === -1) insertIndex = targetChildren.length;

                if (parentNode) {
                    parentNode.children.splice(insertIndex, 0, node);
                }
            };

            const tree = state.selectedUnitGroupTree.tree;

            // Remove the node from its current position
            let movedNode: UnitTreeNode | null = null;
            if (tree.id === unitId) {
                // Moving root node not supported
                return;
            }
            movedNode = removeNode([tree], unitId);
            if (!movedNode) {
                movedNode = removeNode(tree.children, unitId);
            }

            if (!movedNode) return;

            // Find the new parent
            const newParent = newParentId ? findNode(tree, newParentId) : null;

            // Insert at new position
            if (newParent) {
                insertNode(newParent, movedNode, newOrder, tree);
            } else if (!newParentId) {
                // Adding as sibling of root - this case should be handled
                // For now, add to root's children
                insertNode(tree, movedNode, newOrder, tree);
            }
        },
    },
    extraReducers: (builder) => {
        // =================================================================
        // Affiliations
        // =================================================================
        builder
            .addCase(fetchAffiliations.pending, (state) => {
                state.affiliationsLoading = true;
                state.affiliationsError = null;
            })
            .addCase(fetchAffiliations.fulfilled, (state, action) => {
                state.affiliationsLoading = false;
                state.affiliations = action.payload;
            })
            .addCase(fetchAffiliations.rejected, (state, action) => {
                state.affiliationsLoading = false;
                state.affiliationsError = action.payload || 'Failed to fetch affiliations';
            });

        // =================================================================
        // Factions
        // =================================================================
        builder
            .addCase(fetchFactions.pending, (state) => {
                state.factionsLoading = true;
                state.factionsError = null;
            })
            .addCase(fetchFactions.fulfilled, (state, action) => {
                state.factionsLoading = false;
                state.factions = action.payload;
                // Auto-select first faction if none selected
                if (!state.selectedFactionId && action.payload.length > 0) {
                    state.selectedFactionId = action.payload[0].id;
                }
            })
            .addCase(fetchFactions.rejected, (state, action) => {
                state.factionsLoading = false;
                state.factionsError = action.payload || 'Failed to fetch factions';
            });

        builder
            .addCase(createFaction.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createFaction.fulfilled, (state, action) => {
                state.isLoading = false;
                state.factions.push(action.payload);
            })
            .addCase(createFaction.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to create faction';
            });

        builder
            .addCase(updateFaction.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateFaction.fulfilled, (state, action) => {
                state.isLoading = false;
                const index = state.factions.findIndex((f) => f.id === action.payload.id);
                if (index !== -1) {
                    state.factions[index] = action.payload;
                }
            })
            .addCase(updateFaction.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to update faction';
            });

        builder
            .addCase(deleteFaction.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteFaction.fulfilled, (state, action) => {
                state.isLoading = false;
                state.factions = state.factions.filter((f) => f.id !== action.payload);
                if (state.selectedFactionId === action.payload) {
                    state.selectedFactionId = null;
                    state.unitGroups = [];
                    state.selectedUnitGroupId = null;
                    state.selectedUnitGroupTree = null;
                }
            })
            .addCase(deleteFaction.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to delete faction';
            });

        // =================================================================
        // Unit Groups
        // =================================================================
        builder
            .addCase(fetchUnitGroups.pending, (state) => {
                state.unitGroupsLoading = true;
                state.unitGroupsError = null;
            })
            .addCase(fetchUnitGroups.fulfilled, (state, action) => {
                state.unitGroupsLoading = false;
                state.unitGroups = action.payload;
            })
            .addCase(fetchUnitGroups.rejected, (state, action) => {
                state.unitGroupsLoading = false;
                state.unitGroupsError = action.payload || 'Failed to fetch unit groups';
            });

        builder
            .addCase(fetchUnitGroupTree.pending, (state) => {
                state.treeLoading = true;
                state.treeError = null;
            })
            .addCase(fetchUnitGroupTree.fulfilled, (state, action) => {
                state.treeLoading = false;
                state.selectedUnitGroupTree = action.payload;
                state.selectedUnitGroupId = action.payload.unit_group.id;
            })
            .addCase(fetchUnitGroupTree.rejected, (state, action) => {
                state.treeLoading = false;
                state.treeError = action.payload || 'Failed to fetch unit tree';
            });

        builder
            .addCase(createUnitGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createUnitGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.unitGroups.push(action.payload);
            })
            .addCase(createUnitGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to create unit group';
            });

        builder
            .addCase(deleteUnitGroup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteUnitGroup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.unitGroups = state.unitGroups.filter((ug) => ug.id !== action.payload);
                if (state.selectedUnitGroupId === action.payload) {
                    state.selectedUnitGroupId = null;
                    state.selectedUnitGroupTree = null;
                }
            })
            .addCase(deleteUnitGroup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to delete unit group';
            });

        // =================================================================
        // Units
        // =================================================================
        builder
            .addCase(createUnit.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createUnit.fulfilled, (state) => {
                state.isLoading = false;
                // Tree will be refetched after unit creation
            })
            .addCase(createUnit.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to create unit';
            });

        builder
            .addCase(updateUnit.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateUnit.fulfilled, (state) => {
                state.isLoading = false;
                // Tree will be refetched after unit update
            })
            .addCase(updateUnit.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to update unit';
            });

        builder
            .addCase(updateUnitPosition.pending, (state) => {
                // Don't set loading for optimistic updates
            })
            .addCase(updateUnitPosition.fulfilled, (state) => {
                // Tree will be refetched to ensure consistency
            })
            .addCase(updateUnitPosition.rejected, (state, action) => {
                state.error = action.payload || 'Failed to update unit position';
                // Tree should be refetched to rollback optimistic update
            });

        builder
            .addCase(reorderUnits.pending, () => {
                // Don't set loading for optimistic reordering
            })
            .addCase(reorderUnits.fulfilled, () => {
                // Tree will be refetched to ensure consistency
            })
            .addCase(reorderUnits.rejected, (state, action) => {
                state.error = action.payload || 'Failed to reorder units';
                // Tree should be refetched to rollback optimistic reorder
            });

        builder
            .addCase(deleteUnit.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteUnit.fulfilled, (state) => {
                state.isLoading = false;
                // Tree will be refetched after unit deletion
            })
            .addCase(deleteUnit.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to delete unit';
            });
    },
});

// Export actions
export const {
    setSelectedFaction,
    setSelectedUnitGroup,
    clearError,
    clearOrbatState,
    optimisticUpdateTree,
} = orbatSlice.actions;

// Export reducer
export default orbatSlice.reducer;
